import React, { useState } from 'react';
import { copyToClipboard } from '@/shared/utils/exportUtils';
import { captureError } from '@/utils/errorHandler';

const GITHUB_OWNER = "zapt-ai-platform-apps";

export default function AdminGitHubSection({ app, appId, onRefresh, authFetch, showToast }) {
  const appData = app;
  const id = appId;
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(null);
  
  // Check if GitHub repo exists in repositories table
  const githubRepo = appData?.repositories?.find(
    repo => repo.owner === GITHUB_OWNER && repo.name === id
  );
  const githubRepoCreated = !!githubRepo;
  const githubRepoUrl = githubRepoCreated 
    ? `https://github.com/${GITHUB_OWNER}/${id}`
    : null;
  const githubCloneUrl = githubRepoCreated
    ? `https://github.com/${GITHUB_OWNER}/${id}.git`
    : null;

  const handleCreateRepo = async () => {
    if (creating) return;

    try {
      setCreating(true);
      const response = await authFetch('/api/admin/create-github-repo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId: id,
          name: appData?.name,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create GitHub repository');
      }

      const data = await response.json();
      showToast(`✅ ${data.message}`, 'success');
      
      // Refresh app data to get updated githubRepoCreated flag
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      captureError(error, {
        action: 'handleCreateGitHubRepo',
        component: 'AdminGitHubSection',
        extra: { appId: id },
      });
      console.error('Error creating GitHub repo:', error);
      showToast(error.message || 'Failed to create GitHub repository', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleLinkExistingRepo = async () => {
    if (linking) return;

    try {
      setLinking(true);
      const response = await authFetch('/api/admin/link-github-repo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId: id,
          name: appData?.name,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to link GitHub repository');
      }

      const data = await response.json();
      showToast(`✅ ${data.message}`, 'success');
      
      // Refresh app data to get updated githubRepoCreated flag
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      captureError(error, {
        action: 'handleLinkExistingRepo',
        component: 'AdminGitHubSection',
        extra: { appId: id },
      });
      console.error('Error linking GitHub repo:', error);
      showToast(error.message || 'Failed to link GitHub repository', 'error');
    } finally {
      setLinking(false);
    }
  };

  const handleCopyCommand = async (command, label) => {
    const result = await copyToClipboard(command);
    if (result.success) {
      setCopiedCommand(label);
      if (showToast) {
        showToast(`Copied: ${command}`, 'success', 2000);
      }
      setTimeout(() => setCopiedCommand(null), 2000);
    } else {
      if (showToast) {
        showToast('Failed to copy command', 'error');
      }
    }
  };

  const handleCopyAllCommands = async () => {
    const commands = `cd ~/zapt-apps\ngit clone ${githubCloneUrl}`;
    const result = await copyToClipboard(commands);
    if (result.success) {
      setCopiedCommand('all');
      if (showToast) {
        showToast('Copied all commands', 'success', 2000);
      }
      setTimeout(() => setCopiedCommand(null), 2000);
    } else {
      if (showToast) {
        showToast('Failed to copy commands', 'error');
      }
    }
  };

  const handleCopyAllPullCommands = async () => {
    const commands = `cd ~/zapt-apps/${id}\ngit pull`;
    const result = await copyToClipboard(commands);
    if (result.success) {
      setCopiedCommand('pull-all');
      if (showToast) {
        showToast('Copied all pull commands', 'success', 2000);
      }
      setTimeout(() => setCopiedCommand(null), 2000);
    } else {
      if (showToast) {
        showToast('Failed to copy commands', 'error');
      }
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span>🐙</span>
            <span>GitHub Repository</span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage the GitHub repository for this app
          </p>
        </div>
      </div>

      {githubRepoCreated ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✅</span>
              <div>
                <p className="font-medium text-green-900">Repository Created</p>
                <p className="text-sm text-green-700">
                  {GITHUB_OWNER}/{id}
                </p>
                {githubRepo?.isPrimary && (
                  <p className="text-xs text-green-600 mt-1">⭐ Primary repository</p>
                )}
              </div>
            </div>
            <a
              href={githubRepoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
            >
              View on GitHub →
            </a>
          </div>

          {/* Clone Commands Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Initial Setup Commands</h3>
            <p className="text-xs text-gray-500 mb-3">
              Copy these commands to clone the repository to your local machine
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 mb-1">Navigate to zapt-apps folder</p>
                  <code className="text-sm text-gray-800 font-mono bg-white px-2 py-1 rounded border border-gray-300 block">
                    cd ~/zapt-apps
                  </code>
                </div>
                <button
                  onClick={() => handleCopyCommand('cd ~/zapt-apps', 'cd')}
                  className="ml-3 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 flex-shrink-0"
                >
                  {copiedCommand === 'cd' ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 mb-1">Clone the repository</p>
                  <code className="text-sm text-gray-800 font-mono bg-white px-2 py-1 rounded border border-gray-300 block break-all">
                    git clone {githubCloneUrl}
                  </code>
                </div>
                <button
                  onClick={() => handleCopyCommand(`git clone ${githubCloneUrl}`, 'clone')}
                  className="ml-3 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 flex-shrink-0"
                >
                  {copiedCommand === 'clone' ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
              <button
                onClick={handleCopyAllCommands}
                className="w-full px-3 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 text-xs font-medium transition-colors flex items-center justify-center gap-2"
              >
                {copiedCommand === 'all' ? (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Copied All Commands!</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy All Commands</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Pull Latest Changes Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Getting Latest Changes</h3>
            <p className="text-xs text-gray-500 mb-3">
              Copy these commands to pull the latest commits when switching between machines
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 mb-1">Navigate to app directory</p>
                  <code className="text-sm text-gray-800 font-mono bg-white px-2 py-1 rounded border border-gray-300 block">
                    cd ~/zapt-apps/{id}
                  </code>
                </div>
                <button
                  onClick={() => handleCopyCommand(`cd ~/zapt-apps/${id}`, 'cd-app')}
                  className="ml-3 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 flex-shrink-0"
                >
                  {copiedCommand === 'cd-app' ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors group">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 mb-1">Pull latest changes</p>
                  <code className="text-sm text-gray-800 font-mono bg-white px-2 py-1 rounded border border-gray-300 block">
                    git pull
                  </code>
                </div>
                <button
                  onClick={() => handleCopyCommand('git pull', 'pull')}
                  className="ml-3 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 flex-shrink-0"
                >
                  {copiedCommand === 'pull' ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
              <button
                onClick={handleCopyAllPullCommands}
                className="w-full px-3 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 text-xs font-medium transition-colors flex items-center justify-center gap-2"
              >
                {copiedCommand === 'pull-all' ? (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Copied All Commands!</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy All Commands</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-600 mb-4">
              No GitHub repository has been created yet. Create one to get started with version control.
            </p>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={handleCreateRepo}
                disabled={creating || linking}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating ? (
                  <>
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <span>🐙</span>
                    <span>Create GitHub Repo</span>
                  </>
                )}
              </button>
              <button
                onClick={handleLinkExistingRepo}
                disabled={creating || linking}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {linking ? (
                  <>
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    <span>Linking...</span>
                  </>
                ) : (
                  <>
                    <span>🔗</span>
                    <span>Link Existing Repo</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Use "Link Existing Repo" if a GitHub repository already exists for this app (e.g., created manually or through another process).
            </p>
          </div>
        </div>
      )}

    </div>
  );
}

